// Dad Resume (Profile Engine) View Component
import { getCurrentProfile } from '../services/authService';

export function renderDadProfileView(_totalCrewCount: number = 0): string {
  const profile = getCurrentProfile();
  if (!profile) return '';

  const crewCount = profile.crew?.length || 0;

  return `
    <div class="space-y-6 animate-fade-in font-sans">
      
      <!-- Hero Dad Card -->
      <div class="card-workshop p-6 sm:p-7 border-white/[0.08] relative overflow-hidden bg-[#0a0c13]">
        <div class="laser-line-cyan absolute top-0 left-0 right-0"></div>
        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 relative z-10">
          
          <div class="flex items-center gap-4">
            <div class="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-transparent text-cyan-400 font-black text-2xl sm:text-3xl flex items-center justify-center border-2 border-cyan-400 shadow-[0_0_15px_rgba(0,240,255,0.35)] flex-shrink-0">
              ${profile.avatarInitials}
            </div>
            <div>
              <div class="flex items-center gap-2 flex-wrap">
                <h2 class="text-xl sm:text-2xl font-black text-slate-100">
                  ${profile.displayName ? profile.displayName : profile.name}
                </h2>
                <span class="px-2.5 py-0.5 rounded text-[10px] font-bold bg-transparent text-cyan-400 border border-cyan-400/50 shadow-[0_0_6px_rgba(0,240,255,0.2)]">
                  Active Member
                </span>
              </div>
              ${profile.displayName ? `
                <p class="text-[11px] text-slate-400 mt-0.5">Google OAuth Name: <span class="text-slate-300 font-semibold">${profile.name}</span></p>
              ` : ''}
              <p class="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5">
                <i class="fa-solid fa-lock text-[10px] text-cyan-400"></i>
                <span>${profile.email}</span>
                <span class="text-[10px] text-slate-600">&bull;</span>
                <span class="text-[10px] text-cyan-400/80 font-medium">Private (Admin only)</span>
              </p>
              <p class="text-xs text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
                <span><i class="fa-solid fa-location-dot text-cyan-400 mr-1"></i> ${profile.neighborhood || 'Local Area'}</span>
                <span>&bull;</span>
                <span class="text-slate-300 font-semibold">${profile.yearsAsDad} Years as a Dad</span>
              </p>
              <p class="text-xs text-pink-400 mt-1.5 flex items-center gap-1.5 font-medium">
                <i class="fa-solid fa-mug-hot text-pink-400"></i>
                <span>Beverage: ${profile.beverage || 'Favorite drink'}</span>
              </p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div class="text-center px-4 py-2.5 rounded-xl bg-transparent border border-white/[0.08] min-w-[70px]">
              <div class="text-xl font-black text-cyan-400" style="text-shadow: 0 0 8px rgba(0, 240, 255, 0.4);">${profile.badgesEarned.length}</div>
              <div class="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Badges</div>
            </div>
            <div class="text-center px-4 py-2.5 rounded-xl bg-transparent border border-white/[0.08] min-w-[70px]">
              <div class="text-xl font-black text-cyan-400" style="text-shadow: 0 0 8px rgba(0, 240, 255, 0.4);">${crewCount}</div>
              <div class="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Crew Dads</div>
            </div>
            <button id="edit-profile-btn" class="btn-secondary px-3.5 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer">
              <i class="fa-solid fa-pen-to-square text-cyan-400"></i>
              <span>Edit</span>
            </button>
          </div>

        </div>

        <!-- Privacy Shield Callout -->
        <div class="mt-4 p-3 rounded-xl bg-[#06070a] border border-cyan-400/20 text-xs text-slate-400 flex items-start gap-2.5">
          <i class="fa-solid fa-user-shield text-cyan-400 text-sm mt-0.5 flex-shrink-0"></i>
          <p class="text-[11px] leading-relaxed">
            <strong class="text-slate-200">Strict Privacy Active:</strong> Only your <strong>${profile.displayName ? `Display Name ("${profile.displayName}")` : `Name ("${profile.name}")`}</strong> is visible to other dads in the community. All other profile details (email, neighborhood, bio, family squad, and skills) are strictly private to you or accessible only via internal reporting tools by administrators.
          </p>
        </div>

        <div class="mt-4 pt-4 border-t border-white/[0.08] text-xs sm:text-sm text-slate-300 leading-relaxed">
          <strong class="font-bold block mb-1 uppercase tracking-wider text-[11px] text-cyan-400">Dad Bio & Vibe:</strong>
          ${profile.bio || 'Ready to pitch in and collaborate on physical projects.'}
        </div>
      </div>

      <!-- The Family Squad (Privacy-Safe Kid Brackets) -->
      <div class="card-workshop p-6 space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-base font-bold text-slate-200 uppercase tracking-wider flex items-center gap-2">
              <i class="fa-solid fa-children text-pink-400"></i> The Family Squad
            </h3>
            <p class="text-xs text-slate-400 mt-0.5">Privacy first: Broad age brackets and interests for matching playdates; zero sensitive identifiers.</p>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
          ${profile.kids.length === 0 ? `
            <div class="col-span-full p-4 bg-transparent border border-white/[0.06] rounded-xl text-xs text-slate-500 italic text-center">
              No family squad members added yet. Click "Edit" above to coordinate playdates!
            </div>
          ` : profile.kids.map(k => `
            <div class="p-3.5 rounded-xl bg-transparent border border-white/[0.08] hover:border-pink-500/40 text-xs space-y-1 transition-colors">
              <div class="flex items-center justify-between font-bold text-slate-200">
                <span>${k.gender}</span>
                <span class="text-[10px] px-2 py-0.5 rounded-full bg-transparent border border-pink-500/50 text-pink-400">${k.age}</span>
              </div>
              <div class="text-[11px] text-slate-400">
                <span class="text-slate-500 font-medium">Interests:</span> ${k.interest}
              </div>
            </div>
          `).join('')}
        </div>
      </div>

      <!-- Self-Assessed Skill Matrix with Legal Disclaimer -->
      <div class="card-workshop p-6 space-y-4">
        <div>
          <h3 class="text-base font-bold text-slate-200 uppercase tracking-wider flex items-center gap-2">
            <i class="fa-solid fa-screwdriver-wrench text-cyan-400"></i> Self-Assessed Skill Interests
          </h3>
          <div class="mt-2 p-2.5 rounded-xl bg-transparent text-xs text-slate-400 border border-white/[0.08] flex items-start gap-2">
            <i class="fa-solid fa-shield-halved text-cyan-400 text-sm mt-0.5 flex-shrink-0"></i>
            <em>Disclaimer: Skill badges reflect self-reported amateur DIY hobbies and neighborly experience, not licensed trade certification or structural engineering warranty.</em>
          </div>
        </div>

        <div class="flex flex-wrap gap-2 pt-1">
          ${profile.skills.length === 0 ? `
            <div class="text-xs text-slate-500 italic">No skills listed yet. Click "Edit" to list your DIY hobbies.</div>
          ` : profile.skills.map(s => `
            <span class="px-3 py-1.5 rounded-xl bg-transparent text-slate-200 border border-white/[0.1] hover:border-cyan-400/50 text-xs font-semibold flex items-center gap-1.5 transition-colors">
              <i class="fa-solid fa-check text-cyan-400 text-xs"></i> ${s}
            </span>
          `).join('')}
        </div>
      </div>

    </div>
  `;
}

export function bindDadProfileEvents(onEditProfile: () => void): void {
  document.getElementById('edit-profile-btn')?.addEventListener('click', () => {
    onEditProfile();
  });
}
